import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiBox,
  FiDollarSign,
  FiAlertTriangle,
  FiTrendingUp,
  FiTrendingDown,
  FiShoppingCart,
  FiClock,
  FiChevronDown
} from 'react-icons/fi';
import { dashboardService } from '../../services';
import { LoadingGrid } from '../../components/UI/Loading';
import './Dashboard.scss';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedTab, setSelectedTab] = useState('clientes');

  // Datos del dashboard
  const [financialOverview, setFinancialOverview] = useState(null);
  const [topClients, setTopClients] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [salesByYear, setSalesByYear] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    loadTopData();
  }, [selectedPeriod, selectedTab]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Cargar overview financiero
      const financialResult = await dashboardService.getFinancialOverview();
      if (financialResult.success) {
        setFinancialOverview(financialResult.data);
      }

      // Cargar ventas por año
      const salesResult = await dashboardService.getSalesByYear({
        from_year: 2014,
        to_year: new Date().getFullYear()
      });
      if (salesResult.success) {
        setSalesByYear(salesResult.data?.years || []);
      }

      // Cargar dashboard completo para obtener summary
      const dashboardResult = await dashboardService.getDashboard({ period: 'month' });
      if (dashboardResult.success) {
        setSummary(dashboardResult.data?.summary || null);
      }
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTopData = async () => {
    try {
      if (selectedTab === 'clientes') {
        const result = await dashboardService.getTopClients({
          period: selectedPeriod,
          limit: 20
        });
        if (result.success) {
          setTopClients(result.data?.clients || []);
        }
      } else {
        const result = await dashboardService.getTopProducts({
          period: selectedPeriod,
          limit: 20
        });
        if (result.success) {
          setTopProducts(result.data?.products || []);
        }
      }
    } catch (error) {
      console.error('Error cargando top data:', error);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={8} message="Cargando dashboard..." />;
  }

  const currentDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Calcular total para porcentajes
  const topData = selectedTab === 'clientes' ? topClients : topProducts;
  const totalAmount = topData.reduce((sum, item) => sum + (item.total_purchases || item.revenue || 0), 0);

  return (
    <div className="dashboard-page">
      {/* Header de bienvenida */}
      <div className="dashboard-header">
        {/* La imagen de fondo ya incluye el texto "¡HOLA!" */}
      </div>

      {/* Layout principal */}
      <div className="dashboard-main">
        {/* Columna izquierda - Deuda de clientes y órdenes */}
        <div className="left-column">
          <div className="debt-orders-section">
            <div className="section-header">
              <h2 className="section-title">DEUDA DE CLIENTES Y<br/>ÓRDENES SIN MANDAR</h2>
            </div>
            <div className="debt-orders-content">
              <div className="summary-grid">
                <div className="summary-item">
                  <div className="summary-label">Facturas sin pagar</div>
                  <div className="summary-amount">
                    {dashboardService.formatCurrency(financialOverview?.unpaid_invoices?.total_amount || 0)}
                  </div>
                  <div className="summary-count">
                    {financialOverview?.unpaid_invoices?.count || 0}
                  </div>
                </div>
                <div className="summary-item summary-item--danger">
                  <div className="summary-label">Facturas vencidas</div>
                  <div className="summary-amount">
                    {dashboardService.formatCurrency(financialOverview?.overdue_invoices?.total_amount || 0)}
                  </div>
                  <div className="summary-count">
                    {financialOverview?.overdue_invoices?.count || 0}
                  </div>
                </div>
                <div className="summary-item">
                  <div className="summary-label">Órdenes sin enviar</div>
                  <div className="summary-amount">
                    {dashboardService.formatCurrency(financialOverview?.pending_orders?.total_amount || 0)}
                  </div>
                  <div className="summary-count">
                    {financialOverview?.pending_orders?.count || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Columna derecha - Top 20 */}
        <div className="right-column">
          <div className="top-section">
            <div className="section-header">
              <h2 className="section-title">THE TOP 20</h2>
              <div className="period-selector">
                <div className="selector-dropdown" style={{ position: 'relative' }}>
                  <button
                    className="dropdown-btn"
                    onClick={() => {
                      const dropdown = document.querySelector('.dropdown-menu');
                      dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      background: '#ecf0f1',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2c3e50'
                    }}
                  >
                    {selectedTab.toUpperCase()}
                    <FiChevronDown />
                  </button>
                  <div
                    className="dropdown-menu"
                    style={{
                      display: 'none',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '4px',
                      background: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 100,
                      minWidth: '150px'
                    }}
                  >
                    <button
                      onClick={() => {
                        setSelectedTab('clientes');
                        document.querySelector('.dropdown-menu').style.display = 'none';
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: selectedTab === 'clientes' ? '#f0f0f0' : 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        borderRadius: '8px 8px 0 0'
                      }}
                    >
                      CLIENTES
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTab('productos');
                        document.querySelector('.dropdown-menu').style.display = 'none';
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: selectedTab === 'productos' ? '#f0f0f0' : 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        borderRadius: '0 0 8px 8px'
                      }}
                    >
                      PRODUCTOS
                    </button>
                  </div>
                </div>
                <div className="selector-options">
                  <button
                    className={`period-btn ${selectedPeriod === 'month' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('month')}
                  >
                    MES
                  </button>
                  <button
                    className={`period-btn ${selectedPeriod === 'week' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('week')}
                  >
                    SEMANA
                  </button>
                  <button
                    className={`period-btn ${selectedPeriod === 'quarter' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('quarter')}
                  >
                    TRIMESTRE
                  </button>
                  <button
                    className={`period-btn ${selectedPeriod === 'year' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('year')}
                  >
                    AÑO
                  </button>
                </div>
              </div>
            </div>

            {/* Gráfica de dona y lista */}
            <div className="top-content">
              <div className="donut-chart">
                <svg viewBox="0 0 200 200" className="donut-svg">
                  <circle cx="100" cy="100" r="80" fill="none" stroke="#e0e0e0" strokeWidth="40" />
                  {topData.slice(0, 5).map((item, index) => {
                    const percentage = dashboardService.calculatePercentage(
                      item.total_purchases || item.revenue || 0,
                      totalAmount
                    );
                    const offset = topData.slice(0, index).reduce((sum, i) =>
                      sum + dashboardService.calculatePercentage(i.total_purchases || i.revenue || 0, totalAmount), 0
                    );
                    const circumference = 2 * Math.PI * 80;
                    const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                    const rotation = -90 + (offset * 3.6);

                    return (
                      <circle
                        key={index}
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke={dashboardService.getChartColor(index)}
                        strokeWidth="40"
                        strokeDasharray={strokeDasharray}
                        transform={`rotate(${rotation} 100 100)`}
                      />
                    );
                  })}
                </svg>
              </div>

              <div className="top-list-container">
                <div className="top-list">
                  {topData.slice(0, 5).map((item, index) => {
                    const amount = item.total_purchases || item.revenue || 0;
                    const percentage = dashboardService.calculatePercentage(amount, totalAmount);

                    return (
                      <div key={index} className="top-item">
                        <div className="item-info">
                          <div
                            className="item-color"
                            style={{ backgroundColor: dashboardService.getChartColor(index) }}
                          />
                          <div className="item-name">
                            {item.business_name || item.product_name || item.name}
                          </div>
                        </div>
                        <div className="item-stats">
                          <div className="item-value">
                            {dashboardService.formatCurrency(amount)}
                          </div>
                          <div className="item-percentage">
                            {dashboardService.formatPercentage(percentage)}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="see-more">
                    <button className="see-more-btn">
                      <FiChevronDown /> VER MAS
                    </button>
                  </div>

                  <div className="total-section">
                    <div className="total-label">TOTAL</div>
                    <div className="total-amount">
                      {dashboardService.formatCurrency(totalAmount)}
                    </div>
                    <div className="total-percentage">100%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfica de ventas */}
      <div className="sales-chart">
        <div className="chart-header">
          <h2 className="chart-title">VENTAS</h2>
          <div className="chart-controls">
            <button className="year-btn">AÑOS ANTERIORES</button>
            <button className="year-btn active">AÑO ACTUAL</button>
          </div>
        </div>
        <div className="chart-content">
          <div className="chart-bars">
            {salesByYear.map((yearData, index) => {
              const maxSales = Math.max(...salesByYear.map(y => y.total_sales));
              const heightPercent = (yearData.total_sales / maxSales) * 100;

              return (
                <div key={yearData.year} className="year-bar">
                  <div
                    className="bar"
                    style={{
                      height: `${heightPercent}%`,
                      backgroundColor: dashboardService.getChartColor(index)
                    }}
                  >
                    <div className="bar-tooltip">
                      <div className="tooltip-year">{yearData.year}</div>
                      <div className="tooltip-amount">
                        {dashboardService.formatCurrency(yearData.total_sales)}
                      </div>
                    </div>
                  </div>
                  <div className="year-label">{yearData.year}</div>
                </div>
              );
            })}
          </div>
          <div className="chart-scale">
            <div className="scale-line" style={{ bottom: '80%' }}>
              <span className="scale-label">$2,500,000.00</span>
            </div>
            <div className="scale-line" style={{ bottom: '60%' }}>
              <span className="scale-label">$2,000,000.00</span>
            </div>
            <div className="scale-line" style={{ bottom: '40%' }}>
              <span className="scale-label">$1,500,000.00</span>
            </div>
            <div className="scale-line" style={{ bottom: '20%' }}>
              <span className="scale-label">$1,000,000.00</span>
            </div>
            <div className="scale-line" style={{ bottom: '0%' }}>
              <span className="scale-label">$500,000.00</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
